import { CommonModule, formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { Subscription } from 'rxjs';
import { APIListWrapper } from '../../../models/common/api-list-wrapper.model';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { MaintenanceService } from '../maintenance.service';
import { MaintenanceAlert, MaintenanceAnalyticsDashboard, MaintenanceAnalyticsRow } from '../models/maintenance-analytics.model';
import { MaintenanceDashboard, MaintenancePartAvailability, MaintenancePartReorderRecommendation, MaintenanceVehicleAvailability } from '../models/maintenance-availability.model';
import { MaintenanceInspectionDefect, MaintenanceInspectionForm, MaintenanceInspectionSubmission } from '../models/maintenance-inspection.model';
import { MaintenanceDueItem, MaintenanceMeterReading, MaintenanceServiceProgram } from '../models/maintenance-program.model';
import { MaintenanceRequest } from '../models/maintenance-request.model';
import { MaintenanceWorkOrder } from '../models/maintenance-work-order.model';

type MaintenancePage = 'overview' | 'work-orders' | 'preventive' | 'inspections' | 'parts' | 'availability' | 'analytics' | 'vehicle-profile';

@Component({
  selector: 'app-maintenance-workspace',
  standalone: true,
  templateUrl: './maintenance-workspace.component.html',
  styleUrls: ['./maintenance-workspace.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageSearchPanelComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ]
})
export class MaintenanceWorkspaceComponent implements OnInit, OnDestroy {
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  page: MaintenancePage = 'overview';
  isLoading = false;
  keyword = '';
  selectedStatus = '';
  vehicleId?: number;
  detailId?: number;
  analyticsDateRange?: Date[];

  dashboard?: MaintenanceDashboard;
  analytics?: MaintenanceAnalyticsDashboard;
  alerts: MaintenanceAlert[] = [];
  requests: MaintenanceRequest[] = [];
  workOrders: MaintenanceWorkOrder[] = [];
  dueItems: MaintenanceDueItem[] = [];
  programs: MaintenanceServiceProgram[] = [];
  meterReadings: MaintenanceMeterReading[] = [];
  forms: MaintenanceInspectionForm[] = [];
  submissions: MaintenanceInspectionSubmission[] = [];
  defects: MaintenanceInspectionDefect[] = [];
  availability: MaintenanceVehicleAvailability[] = [];
  reorderRecommendations: MaintenancePartReorderRecommendation[] = [];
  selectedWorkOrder?: MaintenanceWorkOrder;
  partAvailability?: MaintenancePartAvailability;

  newRequest: MaintenanceRequest = {};
  newWorkOrder: MaintenanceWorkOrder = { source: 'MANUAL', priority: 'NORMAL', severity: 'MEDIUM', status: 'DRAFT' };
  newProgram: MaintenanceServiceProgram = { status: 'ACTIVE' };
  newReading: MaintenanceMeterReading = { readingSource: 'MANUAL' };
  newForm: MaintenanceInspectionForm = { status: 'ACTIVE' };
  newSubmission: MaintenanceInspectionSubmission = { inspectorRole: 'DRIVER' };
  partAction = { workOrderId: undefined as number | undefined, partLineId: undefined as number | undefined, warehouseItemId: undefined as number | undefined, warehouseId: undefined as number | undefined, quantity: 1, unitCost: undefined as number | undefined };
  availabilityCheck = { vehicleId: undefined as number | undefined, plannedStartAt: undefined as Date | undefined, plannedEndAt: undefined as Date | undefined };

  readonly statusOptions = [
    { label: 'Any status', value: '' },
    { label: 'Open', value: 'OPEN' },
    { label: 'Due Soon', value: 'DUE_SOON' },
    { label: 'Overdue', value: 'OVERDUE' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Waiting Parts', value: 'WAITING_PARTS' },
    { label: 'Out of Service', value: 'OUT_OF_SERVICE' },
    { label: 'Completed', value: 'COMPLETED' }
  ];

  private sub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private maintenanceService: MaintenanceService,
    private sessionStorageService: SessionStorageService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.sub = this.route.data.subscribe(() => {
      this.page = this.route.snapshot.data['page'] ?? 'overview';
      this.detailId = Number(this.route.snapshot.paramMap.get('id')) || undefined;
      this.vehicleId = Number(this.route.snapshot.paramMap.get('vehicleId')) || undefined;
      this.setBreadcrumbs();
      this.loadPage();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  loadPage(): void {
    const companyId = this.companyId();
    if (!companyId) {
      this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
      return;
    }
    this.isLoading = true;
    this.newRequest.owningCompanyId = companyId;
    this.newWorkOrder.owningCompanyId = companyId;
    this.newProgram.owningCompanyId = companyId;
    this.newReading.owningCompanyId = companyId;
    this.newForm.owningCompanyId = companyId;
    this.newSubmission.owningCompanyId = companyId;

    if (this.detailId && this.page === 'work-orders') {
      this.loadWorkOrder(this.detailId);
      return;
    }

    switch (this.page) {
      case 'overview':
        this.loadOverview(companyId);
        break;
      case 'work-orders':
        this.loadWorkOrders(companyId);
        break;
      case 'preventive':
        this.loadPreventive(companyId);
        break;
      case 'inspections':
        this.loadInspections(companyId);
        break;
      case 'parts':
        this.loadParts(companyId);
        break;
      case 'availability':
        this.loadAvailability(companyId);
        break;
      case 'analytics':
        this.loadAnalytics(companyId);
        break;
      case 'vehicle-profile':
        this.loadVehicleProfile(companyId);
        break;
    }
  }

  metrics(): PageMetric[] {
    if (this.page === 'analytics') {
      const a = this.analytics ?? {};
      return [
        { label: 'Total Cost', value: this.money(a.totalCost), hint: 'Actual maintenance cost', tone: 'success' },
        { label: 'Cost / KM', value: this.decimalLabel(a.costPerKilometer), hint: 'Meter-normalized cost', tone: 'info' },
        { label: 'Downtime Hours', value: this.decimalLabel(a.totalDowntimeHours), hint: 'Repair and waiting downtime', tone: 'warn' },
        { label: 'PM Compliance', value: this.percent(a.pmCompliancePercent), hint: 'Completed over due workload', tone: 'info' },
        { label: 'Open Risk Alerts', value: a.alertCount ?? this.alerts.length, hint: 'Computed alerts', tone: (a.criticalAlertCount ?? 0) > 0 ? 'danger' : 'warn' },
        { label: 'Assignment Risk', value: a.assignmentRiskCount ?? 0, hint: 'Warnings, blocks, overrides', tone: 'danger' }
      ];
    }
    const d = this.dashboard ?? {};
    return [
      { label: 'Open Requests', value: d.openRequests ?? this.requests.length, hint: 'Reported issues', tone: 'info' },
      { label: 'Open Work Orders', value: d.openWorkOrders ?? this.workOrders.length, hint: 'Shop workload', tone: 'warn' },
      { label: 'Overdue PM', value: d.overduePmItems ?? this.dueItems.filter(i => i.status === 'OVERDUE').length, hint: 'Preventive maintenance risk', tone: (d.overduePmItems ?? 0) > 0 ? 'danger' : 'default' },
      { label: 'Open Defects', value: d.openDefects ?? this.defects.length, hint: 'Inspection failures', tone: 'warn' },
      { label: 'Out of Service', value: d.outOfServiceVehicles ?? this.availability.filter(i => i.availabilityStatus === 'OUT_OF_SERVICE').length, hint: 'Blocked vehicles', tone: 'danger' },
      { label: 'Actual Cost', value: this.money(d.totalActualCost), hint: 'Maintenance posted/rolled cost', tone: 'success' }
    ];
  }

  createRequest(): void {
    if (!this.newRequest.title || !this.newRequest.vehicleId) {
      this.warn('Request title and vehicle ID are required.');
      return;
    }
    this.maintenanceService.createRequest({ ...this.newRequest, severity: this.newRequest.severity ?? 'MEDIUM', source: this.newRequest.source ?? 'PORTAL' }).subscribe({
      next: () => {
        this.toast('Request created');
        this.newRequest = { owningCompanyId: this.companyId() };
        this.loadPage();
      },
      error: () => this.error('Could not create maintenance request.')
    });
  }

  createWorkOrder(): void {
    if (!this.newWorkOrder.title || !this.newWorkOrder.vehicleId) {
      this.warn('Work order title and vehicle ID are required.');
      return;
    }
    this.maintenanceService.createWorkOrder(this.newWorkOrder).subscribe({
      next: response => {
        this.toast('Work order created');
        this.router.navigate(['/fleet/maintenance/work-orders', response.data?.workOrderId]);
      },
      error: () => this.error('Could not create work order.')
    });
  }

  updateWorkOrderStatus(row: MaintenanceWorkOrder, status: string): void {
    if (!row.workOrderId) {
      return;
    }
    this.maintenanceService.updateWorkOrderStatus(row.workOrderId, { status, comment: `Portal status update to ${status}` }).subscribe({
      next: () => {
        this.toast(`Work order moved to ${status}`);
        this.loadPage();
      },
      error: () => this.error('Could not update work order status.')
    });
  }

  createProgram(): void {
    if (!this.newProgram.programName) {
      this.warn('Program name is required.');
      return;
    }
    this.maintenanceService.createProgram(this.newProgram).subscribe({
      next: () => {
        this.toast('Service program created');
        this.newProgram = { owningCompanyId: this.companyId(), status: 'ACTIVE' };
        this.loadPage();
      },
      error: () => this.error('Could not create PM program.')
    });
  }

  createMeterReading(): void {
    if (!this.newReading.vehicleId || (!this.newReading.odometerKm && !this.newReading.engineHours)) {
      this.warn('Vehicle ID and an odometer or engine-hour reading are required.');
      return;
    }
    this.maintenanceService.createMeterReading({ ...this.newReading, readingTime: new Date() }).subscribe({
      next: () => {
        this.toast('Meter reading saved');
        this.newReading = { owningCompanyId: this.companyId(), readingSource: 'MANUAL' };
        this.loadPage();
      },
      error: () => this.error('Could not save meter reading.')
    });
  }

  recalculateDue(): void {
    this.maintenanceService.recalculateDueItems(this.companyId(), this.vehicleId).subscribe({
      next: () => {
        this.toast('PM schedule recalculated');
        this.loadPage();
      },
      error: () => this.error('Could not recalculate PM schedule.')
    });
  }

  createInspectionForm(): void {
    if (!this.newForm.formName) {
      this.warn('Inspection form name is required.');
      return;
    }
    this.maintenanceService.createInspectionForm(this.newForm).subscribe({
      next: () => {
        this.toast('Inspection form created');
        this.newForm = { owningCompanyId: this.companyId(), status: 'ACTIVE' };
        this.loadPage();
      },
      error: () => this.error('Could not create inspection form.')
    });
  }

  createInspectionSubmission(): void {
    if (!this.newSubmission.formId || !this.newSubmission.vehicleId) {
      this.warn('Form ID and vehicle ID are required.');
      return;
    }
    this.maintenanceService.createInspectionSubmission({ ...this.newSubmission, inspectionTime: new Date() }).subscribe({
      next: () => {
        this.toast('Inspection submitted');
        this.newSubmission = { owningCompanyId: this.companyId(), inspectorRole: 'DRIVER' };
        this.loadPage();
      },
      error: () => this.error('Could not submit inspection.')
    });
  }

  resolveDefect(row: MaintenanceInspectionDefect): void {
    if (!row.defectId) {
      return;
    }
    this.maintenanceService.resolveDefect(row.defectId, { comment: 'Resolved from maintenance portal' }).subscribe({
      next: () => {
        this.toast('Defect resolved');
        this.loadPage();
      },
      error: () => this.error('Could not resolve defect.')
    });
  }

  linkDefect(row: MaintenanceInspectionDefect): void {
    if (!row.defectId) {
      return;
    }
    this.maintenanceService.linkDefectToWorkOrder(row.defectId, { title: row.title, vehicleId: row.vehicleId, vehiclePlateNumber: row.vehiclePlateNumber }).subscribe({
      next: () => {
        this.toast('Defect linked to work order');
        this.loadPage();
      },
      error: () => this.error('Could not link defect.')
    });
  }

  getPartAvailability(): void {
    if (!this.partAction.warehouseItemId) {
      this.warn('Warehouse item ID is required.');
      return;
    }
    this.maintenanceService.getPartAvailability(this.companyId(), this.partAction.warehouseItemId, this.partAction.warehouseId, this.partAction.quantity).subscribe({
      next: response => this.partAvailability = response.data,
      error: () => this.error('Could not load part availability.')
    });
  }

  partOperation(operation: 'reserve' | 'issue' | 'consume' | 'return'): void {
    const { workOrderId, partLineId } = this.partAction;
    if (!workOrderId || !partLineId) {
      this.warn('Work order ID and part line ID are required.');
      return;
    }
    const action = { ...this.partAction, comment: `${operation} from maintenance portal` };
    const request = operation === 'reserve'
      ? this.maintenanceService.reservePart(workOrderId, partLineId, action)
      : operation === 'issue'
        ? this.maintenanceService.issuePart(workOrderId, partLineId, action)
        : operation === 'consume'
          ? this.maintenanceService.consumePart(workOrderId, partLineId, action)
          : this.maintenanceService.returnPart(workOrderId, partLineId, action);
    request.subscribe({
      next: () => {
        this.toast(`Part ${operation} completed`);
        this.loadPage();
      },
      error: () => this.error(`Could not ${operation} part.`)
    });
  }

  checkAvailability(): void {
    if (!this.availabilityCheck.vehicleId) {
      this.warn('Vehicle ID is required.');
      return;
    }
    this.maintenanceService.checkAvailability({
      owningCompanyId: this.companyId(),
      vehicleId: this.availabilityCheck.vehicleId,
      assignmentType: 'PORTAL_CHECK',
      plannedStartAt: this.availabilityCheck.plannedStartAt,
      plannedEndAt: this.availabilityCheck.plannedEndAt,
      blockOverduePm: false
    }).subscribe({
      next: response => this.messageService.add({ severity: response.data?.blockAssignment ? 'error' : response.data?.warning ? 'warn' : 'success', summary: response.data?.availabilityStatus, detail: response.data?.message }),
      error: () => this.error('Could not check vehicle availability.')
    });
  }

  recompute(row: MaintenanceVehicleAvailability): void {
    if (!row.vehicleId) {
      return;
    }
    this.maintenanceService.recomputeAvailability(this.companyId(), row.vehicleId).subscribe({
      next: () => {
        this.toast('Availability recomputed');
        this.loadPage();
      },
      error: () => this.error('Could not recompute availability.')
    });
  }

  openAction(url?: string): void {
    if (url) {
      this.router.navigateByUrl(url);
    }
  }

  openAlert(row: MaintenanceAlert): void {
    this.openAction(row.actionUrl);
  }

  openWorkOrder(row: MaintenanceWorkOrder): void {
    if (row.workOrderId) {
      this.router.navigate(['/fleet/maintenance/work-orders', row.workOrderId]);
    }
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    if (!status) {
      return 'secondary';
    }
    if (['AVAILABLE', 'COMPLETED', 'COST_POSTED', 'RESOLVED'].includes(status)) {
      return 'success';
    }
    if (['OVERDUE', 'OUT_OF_SERVICE', 'CRITICAL', 'REJECTED'].includes(status)) {
      return 'danger';
    }
    if (['WAITING_PARTS', 'WAITING_VENDOR', 'DUE', 'DUE_SOON', 'MAINTENANCE_REVIEW', 'IN_PROGRESS'].includes(status)) {
      return 'warning';
    }
    return 'info';
  }

  dateLabel(value?: string | Date): string {
    return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
  }

  money(value?: number): string {
    return value === undefined || value === null ? '-' : value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }

  decimalLabel(value?: number): string {
    return value === undefined || value === null ? '-' : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  percent(value?: number): string {
    return value === undefined || value === null ? '-' : `${this.decimalLabel(value)}%`;
  }

  analyticsRows(rows?: MaintenanceAnalyticsRow[]): MaintenanceAnalyticsRow[] {
    return rows ?? [];
  }

  pageTitle(): string {
    return {
      overview: 'Maintenance Overview',
      'work-orders': this.detailId ? 'Work Order Detail' : 'Work Orders',
      preventive: 'Preventive Maintenance',
      inspections: 'Inspections & Defects',
      parts: 'Parts & Inventory',
      availability: 'Availability Board',
      analytics: 'Analytics & Alerts',
      'vehicle-profile': 'Vehicle Maintenance Profile'
    }[this.page];
  }

  activeFilterCount(): number {
    return [this.keyword, this.selectedStatus, this.vehicleId, this.analyticsDateRange?.length].filter(Boolean).length;
  }

  private loadOverview(companyId: number): void {
    this.maintenanceService.getDashboard(companyId).subscribe({ next: r => this.dashboard = r.data });
    this.loadWorkOrders(companyId, false);
    this.loadPreventive(companyId, false);
    this.loadInspections(companyId, false);
    this.loadAvailability(companyId, false);
  }

  private loadWorkOrders(companyId: number, manageLoading = true): void {
    if (manageLoading) {
      this.isLoading = true;
    }
    this.maintenanceService.searchWorkOrders(this.filters(companyId)).subscribe(this.listHandler(rows => this.workOrders = rows, manageLoading));
  }

  private loadWorkOrder(id: number): void {
    this.maintenanceService.getWorkOrder(id).subscribe({
      next: response => {
        this.selectedWorkOrder = response.data;
        this.workOrders = response.data ? [response.data] : [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.error('Could not load work order.');
      }
    });
  }

  private loadPreventive(companyId: number, manageLoading = true): void {
    if (manageLoading) {
      this.isLoading = true;
    }
    this.maintenanceService.searchDueItems(this.filters(companyId, 50)).subscribe(this.listHandler(rows => this.dueItems = rows, false));
    this.maintenanceService.searchPrograms(this.filters(companyId, 50)).subscribe(this.listHandler(rows => this.programs = rows, false));
    this.maintenanceService.searchMeterReadings(this.filters(companyId, 50)).subscribe(this.listHandler(rows => this.meterReadings = rows, manageLoading));
  }

  private loadInspections(companyId: number, manageLoading = true): void {
    if (manageLoading) {
      this.isLoading = true;
    }
    this.maintenanceService.searchInspectionForms(this.filters(companyId, 50)).subscribe(this.listHandler(rows => this.forms = rows, false));
    this.maintenanceService.searchInspectionSubmissions(this.filters(companyId, 50)).subscribe(this.listHandler(rows => this.submissions = rows, false));
    this.maintenanceService.searchDefects(this.filters(companyId, 50)).subscribe(this.listHandler(rows => this.defects = rows, manageLoading));
  }

  private loadParts(companyId: number): void {
    this.isLoading = true;
    this.maintenanceService.searchReorderRecommendations({ owningCompanyId: companyId, pageSize: 100, status: this.selectedStatus }).subscribe(this.listHandler(rows => this.reorderRecommendations = rows, false));
    this.maintenanceService.searchWorkOrders({ owningCompanyId: companyId, pageSize: 50, status: this.selectedStatus || 'WAITING_PARTS' }).subscribe(this.listHandler(rows => this.workOrders = rows, true));
  }

  private loadAvailability(companyId: number, manageLoading = true): void {
    if (manageLoading) {
      this.isLoading = true;
    }
    this.maintenanceService.searchAvailability({ ...this.filters(companyId, 100), availabilityStatus: this.selectedStatus }).subscribe(this.listHandler(rows => this.availability = rows, manageLoading));
  }

  private loadAnalytics(companyId: number): void {
    this.isLoading = true;
    const periodFrom = this.analyticsDateRange?.[0];
    const periodTo = this.analyticsDateRange?.[1];
    this.maintenanceService.getAnalytics(companyId, periodFrom, periodTo, this.vehicleId).subscribe({
      next: response => {
        this.analytics = response.data;
        this.alerts = response.data?.alerts ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.error('Could not load maintenance analytics.');
      }
    });
  }

  private loadVehicleProfile(companyId: number): void {
    this.loadWorkOrders(companyId, false);
    this.loadPreventive(companyId, false);
    this.loadInspections(companyId, false);
    this.loadAvailability(companyId, true);
  }

  private filters(companyId: number, pageSize = 100): any {
    return {
      owningCompanyId: companyId,
      page: 0,
      pageSize,
      status: this.selectedStatus,
      keyword: this.keyword,
      vehicleId: this.vehicleId
    };
  }

  private listHandler<T>(assign: (rows: T[]) => void, manageLoading = true) {
    return {
      next: (response: APIListWrapper<T>) => {
        assign(response.items ?? []);
        if (manageLoading) {
          this.isLoading = false;
        }
      },
      error: () => {
        if (manageLoading) {
          this.isLoading = false;
        }
        this.error('Could not load maintenance data.');
      }
    };
  }

  private setBreadcrumbs(): void {
    this.breadcrumbItems = [
      { label: 'Fleet', routerLink: '/fleet' },
      { label: 'Maintenance', routerLink: '/fleet/maintenance' },
      { label: this.pageTitle() }
    ];
  }

  private companyId(): number {
    return this.sessionStorageService.getCompany()?.id ?? 0;
  }

  private toast(detail: string): void {
    this.messageService.add({ severity: 'success', summary: 'Maintenance', detail });
  }

  private warn(detail: string): void {
    this.messageService.add({ severity: 'warn', summary: 'Missing data', detail });
  }

  private error(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Maintenance', detail });
  }
}
